<script>
const CTA_POSITIONS = {
  'left-top': { top: '12px', left: '12px' },
  'center-top': { top: '12px', left: '50%', transform: 'translateX(-50%)' },
  'right-top': { top: '12px', right: '12px' },
  'left-middle': { top: '50%', left: '12px', transform: 'translateY(-50%)' },
  'center-middle': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  'right-middle': { top: '50%', right: '12px', transform: 'translateY(-50%)' },
  'left-bottom': { bottom: 'var(--video-ctas-bottom)', left: '12px' },
  'center-bottom': { bottom: 'var(--video-ctas-bottom)', left: '50%', transform: 'translateX(-50%)' },
  'right-bottom': { bottom: 'var(--video-ctas-bottom)', right: '12px' },
};

const timecodeToSeconds = (timecode) => {
  if (!timecode) {
    return undefined;
  }

  const [minutes, seconds] = timecode.split(':');

  return parseInt(minutes, 10) * 60 + parseInt(seconds, 10);
};

export default {
  props: {
    ctas: {
      type: Array,
      default: () => ([]),
    },
    player: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentTime: 0,
      ctaPaused: {},
    };
  },
  computed: {
    ctaPositions() {
      return CTA_POSITIONS;
    },
    localCtas() {
      return this.ctas.map((cta) => ({
        ...cta,
        startAtSeconds: timecodeToSeconds(cta.startAt),
        endAtSeconds: timecodeToSeconds(cta.endAt),
      }));
    },
    visibleCtas() {
      return this.localCtas.filter((cta) => (
        this.currentTime >= cta.startAtSeconds
        && (!cta.endAtSeconds || this.currentTime <= cta.endAtSeconds)
        && !this.ctaPaused[cta.id]
      ));
    },
  },
  watch: {
    player: {
      immediate: true,
      handler(player, previousPlayer) {
        if (previousPlayer) {
          previousPlayer.off('timeupdate', this.syncCurrentTime);
        }

        if (player) {
          player.on('timeupdate', this.syncCurrentTime);
          this.$nextTick(this.attachToPlayer);
        }
      },
    },
    currentTime(newTime) {
      this.localCtas.forEach((cta) => {
        if (newTime < cta.startAtSeconds && this.ctaPaused[cta.id]) {
          this.ctaPaused[cta.id] = false;
        }
      });

      const ctaToStop = this.localCtas.find((cta) => (
        cta.type === 'STOP'
        && newTime >= cta.startAtSeconds
        && !this.ctaPaused[cta.id]
      ));

      if (ctaToStop) {
        this.player.pause();
      }
    },
  },
  mounted() {
    this.attachToPlayer();
  },
  beforeDestroy() {
    if (this.player) {
      this.player.off('timeupdate', this.syncCurrentTime);
    }

    const { overlay } = this.$refs;

    if (overlay && overlay.parentNode && overlay.parentNode !== this.$el) {
      overlay.parentNode.removeChild(overlay);
    }
  },
  methods: {
    syncCurrentTime() {
      this.currentTime = this.player.currentTime;
    },
    attachToPlayer() {
      const container = this.player && this.player.elements.container;
      const { overlay } = this.$refs;

      // Plyr wraps a <video> in its own container: the overlay must be inside it to sit over the video and show in fullscreen.
      if (container && overlay && !container.contains(overlay)) {
        container.appendChild(overlay);
      }
    },
    resume(cta) {
      this.ctaPaused = { ...this.ctaPaused, [cta.id]: true };
      this.player.play();
    },
    showInfo(cta) {
      return this.$buefy.dialog.alert({
        title: cta.title,
        // Buefy treats a string message as HTML; its VNode slot preserves the
        // same escaped, literal text as the inline CTA body.
        message: [this.$createElement('span', cta.text)],
      });
    },
  },
  i18n: {
    messages: {
      en: {
        see_more: 'see more',
        information: 'Information',
      },
      fr: {
        see_more: 'voir plus',
        information: 'Information',
      },
    },
  },
};
</script>

<template>
  <div class="video-ctas-anchor">
    <div ref="overlay" class="video-ctas">
      <template v-for="cta in visibleCtas">
        <b-button
          v-if="cta.type === 'BUTTON'"
          :key="cta.id"
          tag="a"
          type="is-primary is-custom"
          class="is-absolute"
          style="z-index: 10;transition: bottom 0.4s;"
          :style="ctaPositions[cta.position]"
          :href="cta.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ cta.text }}
        </b-button>

        <article
          v-else-if="cta.type === 'INFO'"
          :key="cta.id"
          class="card is-absolute has-text-left"
          style="z-index: 10;transition: bottom 0.4s;max-width: 300px;"
          :style="ctaPositions[cta.position]"
        >
          <header v-if="cta.title || cta.isClosed" class="card-header">
            <h3 class="card-header-title px-3 py-2 p-2-mobile has-text-primary is-custom is-size-8-mobile">
              {{ cta.title ? cta.title : $t('information') }}
              <template v-if="cta.isClosed">
                -
                <a
                  href="#"
                  class="is-custom tdecoration-underline ml-1"
                  v-t="'see_more'"
                  @click.prevent="showInfo(cta)"
                />
              </template>
            </h3>
          </header>
          <div v-if="cta.text" v-show="!cta.isClosed" class="card-content p-3 p-2-mobile">
            <div class="content is-size-8-mobile">
              <p>{{ cta.text }}</p>
            </div>
          </div>
        </article>

        <article
          v-else-if="cta.type === 'CARD'"
          :key="cta.id"
          class="card is-absolute has-text-left"
          style="z-index: 10;transition: bottom 0.4s;max-width: 300px;"
          :style="ctaPositions[cta.position]"
        >
          <header v-if="cta.title" class="card-header">
            <h3 class="card-header-title px-3 py-2 has-text-primary is-custom">
              {{ cta.title }}
            </h3>
          </header>
          <div v-if="cta.text" class="card-content p-3">
            <div class="content">
              <p>{{ cta.text }}</p>
            </div>
          </div>
          <footer v-if="cta.ctaText && cta.ctaUrl" class="card-footer">
            <div class="w-full py-2 px-3 has-text-right">
              <a
                class="has-text-primary is-custom has-text-weight-bold tdecoration-underline"
                :href="cta.ctaUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ cta.ctaText }}
              </a>
            </div>
          </footer>
        </article>

        <article
          v-else-if="cta.type === 'STOP'"
          :key="cta.id"
          class="
            box is-absolute has-text-centered
            is-flex is-flex-direction-column is-justify-content-center
          "
          style="z-index:11;top:12px;left:12px;right:12px;bottom:12px;"
        >
          <button
            type="button"
            class="delete is-absolute"
            style="top:12px;right:12px;"
            @click="resume(cta)"
          />
          <h3 v-if="cta.title" class="title has-text-primary is-custom is-size-5-touch mb-2-touch">
            {{ cta.title }}
          </h3>
          <div v-if="cta.text" class="content mb-2-touch">
            <p>{{ cta.text }}</p>
          </div>
          <footer v-if="cta.ctaText && cta.ctaUrl">
            <b-button
              tag="a"
              type="is-primary is-custom"
              :href="cta.ctaUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ cta.ctaText }}
            </b-button>
          </footer>
        </article>
      </template>
    </div>
  </div>
</template>

<style>
.video-ctas-anchor,
.video-ctas {
  display: contents;
}

.video-ctas {
  --video-ctas-bottom: 64px;
}

.plyr--hide-controls .video-ctas {
  --video-ctas-bottom: 12px;
}
</style>
